import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Lock, Sparkles, ArrowRight, Trophy, MapPin } from "lucide-react";

import { Navbar } from "@/components/layout/Nav";
import { CanalesLogo } from "@/components/CanalesLogo";
import { Mascot, Carlos } from "@/components/characters/Characters";
import { useLang } from "@/context/LangContext";
import { api } from "@/api/client";

const STAGE_ICON_COLOR = ["#005BBB", "#E3000F", "#8B5CF6", "#0EA5E9", "#FFB800", "#4CAF50"];

const StageCard = ({ stage, index, progress, lang, active, done, gold }) => {
  const color = stage.color || STAGE_ICON_COLOR[index % STAGE_ICON_COLOR.length];
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06 }}
      className={`brutal p-5 bg-white relative ${gold ? "ring-4 ring-canales-yellow" : ""}`}
      data-testid={`onboarding-stage-${stage.key}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-14 h-14 rounded-2xl border-2 border-black flex items-center justify-center text-white font-display font-black text-xl shrink-0"
          style={{ background: color }}
        >
          {stage.stage}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-display font-bold uppercase tracking-widest text-neutral-600">
            {lang === "es" ? "Etapa" : "Stage"} {stage.stage}
          </div>
          <h3 className="font-display font-black text-xl truncate">
            {lang === "es" ? stage.label_es : stage.label_en}
          </h3>
          <div className="mt-1 text-sm font-body">
            {stage.exams.length} {lang === "es" ? "examen(es)" : "exam(s)"}
          </div>
        </div>
        <div className="text-right">
          {done ? (
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          ) : active ? (
            <Sparkles className="w-7 h-7 text-canales-yellow" />
          ) : (
            <Lock className="w-6 h-6 text-neutral-400" />
          )}
        </div>
      </div>

      <ul className="mt-4 space-y-2">
        {stage.exams.map((e) => {
          const p = progress?.[e.exam_id];
          const pct = p?.best_percent ?? 0;
          const passed = p?.passed;
          return (
            <li
              key={e.exam_id}
              className="flex items-center justify-between brutal-sm bg-canales-cream px-3 py-2"
            >
              <div className="min-w-0">
                <div className="font-display font-bold text-sm truncate">
                  {lang === "es" ? e.title_es : e.title_en}
                </div>
                <div className="text-xs font-body text-neutral-600">
                  {p?.attempts ? `${p.attempts} ${lang === "es" ? "intentos" : "attempts"}` : lang === "es" ? "Sin intentar" : "Not attempted"}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div
                  className={`font-display font-black text-sm ${
                    pct >= 90 ? "text-canales-red" : passed ? "text-green-700" : "text-neutral-600"
                  }`}
                >
                  {pct ? `${pct}%` : "—"}
                </div>
                <Link
                  to={`/exam/${e.slug}`}
                  className="brutal-btn bg-canales-blue text-white px-2 py-1 text-xs flex items-center gap-1"
                  data-testid={`onboarding-open-${e.slug}`}
                >
                  {lang === "es" ? "Abrir" : "Open"} <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
};

export default function Onboarding() {
  const { lang, t } = useLang();
  const [route, setRoute] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem("canales_employee") || "");
  const [inputName, setInputName] = useState(name);
  const [progress, setProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/public/onboarding/route");
      setRoute(data.route || []);
    })();
  }, []);

  const loadProgress = async (n) => {
    if (!n) return;
    setLoadingProgress(true);
    try {
      const { data } = await api.get(`/public/onboarding/progress/${encodeURIComponent(n)}`);
      setProgress(data);
    } finally {
      setLoadingProgress(false);
    }
  };

  useEffect(() => {
    if (name) loadProgress(name);
  }, [name]);

  const progressMap = useMemo(() => {
    if (!progress) return {};
    const m = {};
    progress.stages.forEach((s) => s.exams.forEach((e) => (m[e.exam_id] = e)));
    return m;
  }, [progress]);

  // active stage = first not completed
  const activeIndex = useMemo(() => {
    if (!progress) return 0;
    const idx = progress.stages.findIndex((s) => !s.completed);
    return idx === -1 ? progress.stages.length : idx;
  }, [progress]);

  const submitName = (e) => {
    e.preventDefault();
    const clean = inputName.trim();
    if (!clean) return;
    localStorage.setItem("canales_employee", clean);
    setName(clean);
  };

  return (
    <div className="min-h-screen bg-halftone" data-testid="onboarding-root">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="brutal p-6 bg-canales-blue text-white flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <CanalesLogo width={140} />
            <div>
              <div className="inline-flex items-center gap-2 bg-canales-yellow text-black px-3 py-1 rounded-full border-2 border-black font-display font-bold text-xs uppercase tracking-widest">
                <MapPin className="w-4 h-4" /> {lang === "es" ? "Ruta de Onboarding" : "Onboarding Path"}
              </div>
              <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl">
                {lang === "es"
                  ? "Tu camino para ser un Experto Canales"
                  : "Your path to become a Canales Expert"}
              </h1>
              <p className="font-body opacity-90 mt-1 max-w-2xl">
                {lang === "es"
                  ? "Completa las 6 etapas en orden. Aprueba con 90% o más en cada examen para ganar el certificado dorado."
                  : "Complete all 6 stages in order. Score 90%+ on every exam to earn the golden certificate."}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Mascot size={90} />
          </div>
        </div>

        {/* Name gate */}
        <form onSubmit={submitName} className="mt-6 brutal p-4 bg-white flex flex-wrap items-center gap-3">
          <label className="font-display font-bold">
            {lang === "es" ? "Tu nombre:" : "Your name:"}
          </label>
          <input
            data-testid="onboarding-name-input"
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            placeholder={lang === "es" ? "Ej. María Pérez" : "e.g. Maria Perez"}
            className="brutal-sm px-3 py-2 font-body flex-1 min-w-[180px]"
          />
          <button
            type="submit"
            data-testid="onboarding-track-btn"
            className="brutal-btn bg-canales-red text-white px-4 py-2 font-display font-bold"
          >
            {lang === "es" ? "Ver mi progreso" : "Show my progress"}
          </button>
          {progress && (
            <div className="flex items-center gap-2 ml-auto">
              <Trophy className="w-5 h-5" />
              <span className="font-display font-bold">
                {progress.passed_exams}/{progress.total_exams} · {progress.gold_exams}⭐
              </span>
              {progress.golden && (
                <span
                  data-testid="onboarding-cert-eligible"
                  className="brutal-btn bg-canales-yellow text-black px-3 py-1 text-sm font-display font-bold border-black inline-flex items-center gap-1"
                  title={lang === "es" ? "Pide al admin que te emita el certificado" : "Ask admin to issue your certificate"}
                >
                  🏆 {lang === "es" ? "¡Listo para certificado!" : "Ready for certificate!"}
                </span>
              )}
            </div>
          )}
        </form>

        {loadingProgress && <div className="mt-2 font-body text-sm">…</div>}

        {/* Stages timeline */}
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {route.map((stage, i) => (
            <StageCard
              key={stage.key}
              stage={stage}
              index={i}
              progress={progressMap}
              lang={lang}
              active={i === activeIndex}
              done={progress?.stages?.[i]?.completed}
              gold={progress?.stages?.[i]?.gold}
            />
          ))}
        </div>

        {/* Bottom banner */}
        <div className="mt-8 brutal p-6 bg-canales-yellow flex items-center gap-4">
          <Carlos size={90} waving />
          <div className="flex-1">
            <div className="font-display font-black text-2xl">
              {lang === "es" ? "¿Quieres el certificado dorado?" : "Want the golden certificate?"}
            </div>
            <p className="font-body">
              {lang === "es"
                ? "Aprueba los 9 exámenes con al menos 90% y pide a tu admin que emita tu certificado dorado 'Experto Canales'."
                : "Pass all 9 exams with at least 90% and ask your admin to issue your 'Canales Expert' certificate."}
            </p>
          </div>
          {progress?.golden ? (
            <div className="brutal-sm bg-canales-red text-white px-4 py-2 font-display font-black text-sm">
              🏆 {lang === "es" ? "¡Listo!" : "Ready!"}
            </div>
          ) : (
            <div className="brutal-sm bg-white px-4 py-2 font-display font-bold text-sm">
              {progress ? `${progress.gold_exams}/${progress.total_exams} 90%+` : "?"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
